import { useState, useRef, useEffect, useId, type KeyboardEvent } from 'react';
import { MapPin, Navigation, Bookmark } from 'lucide-react';
import { MapContainer, TileLayer, Marker, useMap } from 'react-leaflet';
import L from 'leaflet';
import { Spinner } from './Spinner';
import { Button } from './Button';
import { useToast } from './Toast';
import type { SavedLocation } from '../types/app';

// ── Types ──────────────────────────────────────────────────────────────────────

export interface PhotonFeature {
  geometry: { coordinates: [number, number] };
  properties: {
    name?: string; city?: string; state?: string; country?: string;
    street?: string; housenumber?: string;
  };
}

// ── Helpers ────────────────────────────────────────────────────────────────────

export function formatSuggestion(f: PhotonFeature): string {
  const p = f.properties;
  const street = p.housenumber && p.street ? `${p.housenumber} ${p.street}` : p.street;
  const parts = [street ?? p.name, p.city, p.state].filter(Boolean);
  return [...new Set(parts)].join(', ');
}

export async function photonSearch(query: string): Promise<PhotonFeature[]> {
  if (query.length < 3) return [];
  const params = new URLSearchParams({
    q: query, limit: '5', lang: 'en',
    bbox: '-78.5,17.4,-76.0,18.6',
  });
  const res = await fetch(`https://photon.komoot.io/api/?${params}`);
  const json = await res.json() as { features?: PhotonFeature[] };
  return json.features ?? [];
}

async function reverseGeocode(lat: number, lng: number): Promise<string | null> {
  try {
    const res = await fetch(`https://photon.komoot.io/reverse?lat=${lat}&lon=${lng}&limit=1`);
    const json = await res.json() as { features?: PhotonFeature[] };
    const feat = json.features?.[0];
    return feat ? formatSuggestion(feat) : null;
  } catch {
    return null;
  }
}

// ── Custom amber pin icon (avoids broken default Leaflet icon in Vite) ──────────

const pinIcon = L.divIcon({
  className: '',
  html: '<div style="width:14px;height:14px;background:#e2a626;border-radius:50%;border:2px solid #fff;box-shadow:0 1px 4px rgba(0,0,0,0.4)"></div>',
  iconSize:   [14, 14],
  iconAnchor: [7, 7],
});

// ── MapRecenter — re-centers map when coords change ────────────────────────────

function MapRecenter({ coords }: { coords: [number, number] }) {
  const map = useMap();
  useEffect(() => {
    map.setView(coords, map.getZoom(), { animate: true });
  }, [coords]); // eslint-disable-line react-hooks/exhaustive-deps
  return null;
}

// ── DraggableMarker ────────────────────────────────────────────────────────────

interface DraggableMarkerProps {
  coords: [number, number];
  onDragEnd: (lat: number, lng: number) => void;
}

function DraggableMarker({ coords, onDragEnd }: DraggableMarkerProps) {
  const markerRef = useRef<L.Marker>(null);

  return (
    <Marker
      position={coords}
      icon={pinIcon}
      draggable
      ref={markerRef}
      eventHandlers={{
        dragend: () => {
          const m = markerRef.current;
          if (m) {
            const { lat, lng } = m.getLatLng();
            onDragEnd(lat, lng);
          }
        },
      }}
    />
  );
}

// ── AddressField ───────────────────────────────────────────────────────────────

const LABEL_CLS = 'block text-[11px] font-bold text-muted uppercase tracking-widest';

export interface AddressFieldProps {
  label: string;
  value: string;
  placeholder?: string;
  onChange: (address: string, coords: [number, number] | null) => void;
  locked?: boolean;
  savedLocations?: SavedLocation[];
}

export function AddressField({ label, value, placeholder, onChange, locked, savedLocations }: AddressFieldProps) {
  const [query, setQuery]             = useState(value);
  const [suggestions, setSuggestions] = useState<PhotonFeature[]>([]);
  const [open, setOpen]               = useState(false);
  const [showSaved, setShowSaved]     = useState(false);
  const [loading, setLoading]         = useState(false);
  const [searchError, setSearchError] = useState(false);
  const [coords, setCoords]           = useState<[number, number] | null>(null);
  const [activeIndex, setActiveIndex] = useState(-1);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const savedRef = useRef<HTMLDivElement>(null);
  const requestSeqRef = useRef(0);
  const listboxId = useId();
  const { toast } = useToast();

  useEffect(() => {
    if (!value) { setQuery(''); setCoords(null); }
  }, [value]);

  // Close saved dropdown on outside click
  useEffect(() => {
    if (!showSaved) return;
    const handler = (e: MouseEvent) => {
      if (savedRef.current && !savedRef.current.contains(e.target as Node)) {
        setShowSaved(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [showSaved]);

  // Tracks the portal's dark-mode toggle (useDarkMode sets/removes a `.dark` class on <html>) so
  // the mini-map's tile layer can follow it — a MutationObserver rather than a shared context
  // since useDarkMode's own state is local to whichever component renders the toggle.
  const [mapDark, setMapDark] = useState(() => document.documentElement.classList.contains('dark'));
  useEffect(() => {
    const observer = new MutationObserver(() => setMapDark(document.documentElement.classList.contains('dark')));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['class'] });
    return () => observer.disconnect();
  }, []);

  const handleInput = (q: string) => {
    setQuery(q);
    setCoords(null);
    setShowSaved(false);
    setSearchError(false);
    setActiveIndex(-1);
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      const seq = ++requestSeqRef.current;
      setLoading(true);
      try {
        const results = await photonSearch(q);
        if (seq !== requestSeqRef.current) return; // a newer request already superseded this one
        setSuggestions(results);
        setOpen(q.length >= 3);
        setSearchError(false);
      } catch {
        if (seq !== requestSeqRef.current) return;
        setSuggestions([]);
        setOpen(true);
        setSearchError(true);
      } finally {
        if (seq === requestSeqRef.current) setLoading(false);
      }
    }, 350);
  };

  const handleInputKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (!open || suggestions.length === 0) return;
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIndex((i) => (i + 1) % suggestions.length);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIndex((i) => (i <= 0 ? suggestions.length - 1 : i - 1));
    } else if (e.key === 'Enter' && activeIndex >= 0) {
      e.preventDefault();
      handleSelect(suggestions[activeIndex]);
    } else if (e.key === 'Escape') {
      setOpen(false);
    }
  };

  const handleSelect = (f: PhotonFeature) => {
    const addr = formatSuggestion(f);
    const [lng, lat] = f.geometry.coordinates;
    setQuery(addr);
    setSuggestions([]);
    setOpen(false);
    setCoords([lat, lng]);
    onChange(addr, [lat, lng]);
  };

  const handleSavedSelect = (loc: SavedLocation) => {
    setShowSaved(false);
    setQuery(loc.address);
    setSuggestions([]);
    setOpen(false);
    if (loc.lat != null && loc.lng != null) {
      const c: [number, number] = [loc.lat, loc.lng];
      setCoords(c);
      onChange(loc.address, c);
    } else {
      setCoords(null);
      onChange(loc.address, null);
    }
  };

  const handleGPS = () => {
    if (!navigator.geolocation) {
      toast('Location services are not available in this browser.', 'error');
      return;
    }
    navigator.geolocation.getCurrentPosition(async (pos) => {
      const { latitude: lat, longitude: lng } = pos.coords;
      const addr = await reverseGeocode(lat, lng) ?? `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
      setQuery(addr);
      setCoords([lat, lng]);
      onChange(addr, [lat, lng]);
    }, (err) => {
      toast(
        err.code === err.PERMISSION_DENIED
          ? 'Location permission denied. Enable it in your browser settings and try again.'
          : 'Could not get your current location. Please try again.',
        'error',
      );
    });
  };

  const handleMarkerDrag = async (lat: number, lng: number) => {
    setCoords([lat, lng]);
    const addr = await reverseGeocode(lat, lng) ?? `${lat.toFixed(5)}, ${lng.toFixed(5)}`;
    setQuery(addr);
    onChange(addr, [lat, lng]);
  };

  const hasSaved = savedLocations && savedLocations.length > 0;

  if (locked) {
    return (
      <div>
        <p className={`${LABEL_CLS} mb-2`}>{label}</p>
        <div className="flex items-center gap-2 rounded-xl border border-line bg-surface-raised px-4 py-2.5">
          <MapPin size={14} className="text-muted flex-shrink-0" />
          <p className="text-sm text-muted">{value}</p>
        </div>
      </div>
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-2">
        <p className={LABEL_CLS}>{label}</p>
        {hasSaved && (
          <div className="relative" ref={savedRef}>
            <button
              type="button"
              onClick={() => setShowSaved((v) => !v)}
              className={`flex items-center gap-1 text-[10px] font-bold uppercase tracking-widest px-2 py-0.5 rounded-lg transition-colors ${
                showSaved ? 'bg-accent text-black' : 'text-accent hover:bg-accent/10'
              }`}
            >
              <Bookmark size={10} />
              Saved
            </button>
            {showSaved && (
              <div className="absolute right-0 top-full mt-1 z-50 w-64 bg-surface border border-line rounded-2xl shadow-lg overflow-hidden">
                <p className="px-3 pt-2.5 pb-1.5 text-[10px] font-bold text-muted uppercase tracking-widest border-b border-line">
                  Saved locations
                </p>
                {savedLocations!.map((loc) => (
                  <button
                    key={loc.id}
                    type="button"
                    onMouseDown={() => handleSavedSelect(loc)}
                    className="w-full text-left px-3 py-2.5 hover:bg-surface-raised transition-colors border-b border-line last:border-0"
                  >
                    <p className="text-sm font-semibold text-text truncate">{loc.label}</p>
                    <p className="text-xs text-muted truncate mt-0.5">{loc.address}</p>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      <div className="relative flex gap-2">
        <div className="relative flex-1">
          <MapPin size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted pointer-events-none" />
          <input
            value={query}
            onChange={(e) => handleInput(e.target.value)}
            onFocus={() => suggestions.length > 0 && setOpen(true)}
            onBlur={() => setTimeout(() => setOpen(false), 150)}
            onKeyDown={handleInputKeyDown}
            placeholder={placeholder ?? 'Search address…'}
            role="combobox"
            aria-expanded={open}
            aria-controls={listboxId}
            aria-autocomplete="list"
            aria-activedescendant={activeIndex >= 0 ? `${listboxId}-option-${activeIndex}` : undefined}
            className="w-full rounded-xl border border-line bg-surface-raised pl-9 pr-3 py-2.5 text-sm text-text placeholder-muted outline-none focus:border-accent transition-colors"
          />
          {loading && (
            <div className="absolute right-3 top-1/2 -translate-y-1/2">
              <Spinner size="sm" />
            </div>
          )}
          {open && !loading && (searchError || suggestions.length > 0 || query.length >= 3) && (
            <div id={listboxId} role="listbox" className="absolute top-full left-0 right-0 mt-1 z-50 bg-surface border border-line rounded-xl shadow-lg overflow-hidden">
              {searchError ? (
                <p className="px-4 py-3 text-sm text-muted">Couldn't load suggestions, try again.</p>
              ) : suggestions.length > 0 ? (
                suggestions.map((f, i) => (
                  <button
                    key={i}
                    id={`${listboxId}-option-${i}`}
                    role="option"
                    aria-selected={i === activeIndex}
                    type="button"
                    onMouseDown={() => handleSelect(f)}
                    onMouseEnter={() => setActiveIndex(i)}
                    className={`w-full text-left px-4 py-2.5 text-sm transition-colors border-b border-line last:border-0 ${
                      i === activeIndex ? 'bg-accent-bg' : 'hover:bg-accent-bg'
                    }`}
                  >
                    <p className="font-medium text-text">{formatSuggestion(f)}</p>
                    <p className="text-xs text-muted">{f.properties.country}</p>
                  </button>
                ))
              ) : (
                <p className="px-4 py-3 text-sm text-muted">No matches found.</p>
              )}
            </div>
          )}
        </div>
        <Button
          type="button"
          variant="secondary"
          size="icon-md"
          onClick={handleGPS}
          className="flex-shrink-0 !h-10 !w-10 rounded-xl !bg-transparent hover:!bg-transparent hover:!text-accent hover:!border-accent"
          aria-label="Use current location"
          title="Use current location"
        >
          <Navigation size={15} />
        </Button>
      </div>

      {coords && (
        <div className="element-enter mt-2 rounded-xl border border-line overflow-hidden" style={{ height: 180 }}>
          <MapContainer
            center={coords}
            zoom={15}
            scrollWheelZoom
            style={{ height: '100%', width: '100%' }}
            zoomControl
          >
            <TileLayer
              url={`https://{s}.basemaps.cartocdn.com/${mapDark ? 'dark_all' : 'light_all'}/{z}/{x}/{y}{r}.png`}
              attribution='&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors &copy; <a href="https://carto.com/">CARTO</a>'
            />
            <MapRecenter coords={coords} />
            <DraggableMarker coords={coords} onDragEnd={handleMarkerDrag} />
          </MapContainer>
        </div>
      )}
    </div>
  );
}
